import React, { useState } from 'react';
import InputForm from './components/InputForm';
import LanguageSelector from './components/LanguageSelector';
import AnswerSection from './components/AnswerSection';
import CopyButton from './components/CopyButton';
import { Sparkles, FileCode2 } from 'lucide-react';

const App = () => {
  const [question, setQuestion] = useState('');
  const [constraints, setConstraints] = useState('');
  const [testCase, setTestCase] = useState('');
  const [language, setLanguage] = useState('C++');
  const [fetchedCode, setFetchedCode] = useState('');

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const prompt = `
Generate a ${language} solution for the following coding question:
Question: ${question}
Constraints: ${constraints}
Test Case: ${testCase}

Instructions:
- Only generate the solution function. Do NOT include the main function, #include statements, using namespace std, or std::.
- Only return the code without any explanation or additional text.
- The code should be well-structured, efficient, and follow best practices.
- It should be well-commented and easy to understand.
- It must handle edge cases and be thoroughly tested.
`;

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=AIzaSyCAFLlKddd7gUKFzvjT6TfBFGXLnfnitqQ`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [{ text: prompt }],
              },
            ],
          }),
        }
      );

      const data = await response.json();
      const code = data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response received.';
      setFetchedCode(code);
    } catch (error) {
      console.error('Error fetching code:', error);
      setFetchedCode('Error fetching code.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-100 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-10">
        <header className="mb-8 text-center">
          <div className="flex justify-center items-center gap-3">
            <Sparkles className="text-purple-600 w-8 h-8" />
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
              AlgoForge AI
            </h1>
          </div>
          <p className="mt-2 text-gray-600 text-sm">Generate optimized code instantly from your question.</p>
        </header>

        <form onSubmit={handleFormSubmit} className="space-y-6">
          <InputForm
            question={question}
            setQuestion={setQuestion}
            constraints={constraints}
            setConstraints={setConstraints}
            testCase={testCase}
            setTestCase={setTestCase}
            onSubmit={handleFormSubmit}
          />

          <LanguageSelector language={language} setLanguage={setLanguage} />

          <div className="flex justify-center">
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-6 py-2 rounded-xl font-semibold shadow-md transition-all"
            >
              Generate Code
            </button>
          </div>
        </form>

        {fetchedCode && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-2">
              <FileCode2 className="text-blue-600 w-5 h-5" />
              <h2 className="text-lg font-semibold text-gray-800">Generated Code:</h2>
            </div>
            <AnswerSection code={fetchedCode} />
            <div className="mt-2 flex justify-end">
              <CopyButton code={fetchedCode} />
            </div>
          </div>
        )}
      </div>

      <footer className="mt-10 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} AlgoForge AI. All rights reserved.
      </footer>
    </div>
  );
};

export default App;
